
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BroData } from '@/lib/broCode';
import { estimateLiquidityValue } from '@/lib/liquidityProvider';
import { Loader2 } from 'lucide-react';

interface BrometheusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentBrocode: string;
  isLoading: boolean;
  broData: BroData | null;
  brometheusAvailable: boolean;
  onClaimWithXp: () => void;
  onClaimWithTokens: () => void;
  onClaimWithLiquidity: () => void;
}

const BrometheusDialog = ({
  isOpen,
  onClose,
  currentBrocode,
  isLoading,
  broData,
  onClaimWithXp,
  onClaimWithTokens,
  onClaimWithLiquidity
}: BrometheusDialogProps) => {
  const [estimatedBnb, setEstimatedBnb] = useState<string>('');
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    const getEstimate = async () => {
      if (isOpen) {
        setEstimating(true);
        try {
          const bnbAmount = await estimateLiquidityValue('50000');
          setEstimatedBnb(bnbAmount);
        } catch (error) {
          console.error('Error estimating BNB:', error);
          setEstimatedBnb('1.0'); // Fallback
        } finally {
          setEstimating(false);
        }
      }
    };
    
    getEstimate();
  }, [isOpen]);

  // Reset loading states when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setEstimating(false);
    }
  }, [isOpen]);

  const currentBroScore = broData?.bro_score || 0;
  const canClaimWithXp = currentBroScore >= 5000;

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-navy-light border border-tan-dark text-tan max-w-md">
        <DialogHeader>
          <DialogTitle className="text-tan text-xl font-patrick">Claim BROMETHEUS</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-tan/80">Current BroCode: <span className="font-mono text-tan">{currentBrocode}</span></p>
          <p className="text-sm text-tan/80">Current BroScore: <span className="text-tan">{currentBroScore}</span></p>
          <p className="text-sm text-orange-400">⚡ Claiming BROMETHEUS awards +1000 BroScore!</p>
          
          <div className="space-y-3">
            <Button 
              onClick={onClaimWithXp}
              disabled={!canClaimWithXp || isLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Claim with 5,000 BroScore
            </Button>
            
            <Button 
              onClick={onClaimWithTokens}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 text-white hover:from-yellow-700 hover:to-orange-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Claim with 100,000 TOP1 Tokens
            </Button>

            <Button 
              onClick={onClaimWithLiquidity}
              disabled={isLoading || estimating}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Claim + Add Liquidity
              <div className="text-xs mt-1">
                {estimating ? (
                  'Calculating...'
                ) : (
                  `50,000 TOP1 + ${estimatedBnb ? `${parseFloat(estimatedBnb).toFixed(3)}` : '~1.0'} BNB`
                )}
              </div>
            </Button>
            
            {!canClaimWithXp && (
              <p className="text-xs text-red-400">Insufficient BroScore for XP claim (need 5,000)</p>
            )}
          </div>
          
          <Button 
            onClick={handleClose}
            variant="outline"
            disabled={isLoading}
            className="w-full border-tan text-tan hover:bg-tan hover:text-navy-dark"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BrometheusDialog;
